import type { KeyboardEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { formatTimeTo12Hour } from '@/lib/utils/time-validation'
import { CaretDownIcon } from '@phosphor-icons/react'

const TIME_OPTION_INCREMENT_MINUTES = 15
const TIME_OPTION_HEIGHT = 36

const TIME_OPTIONS = Array.from(
  { length: (24 * 60) / TIME_OPTION_INCREMENT_MINUTES },
  (_, index) => {
    const totalMinutes = index * TIME_OPTION_INCREMENT_MINUTES
    const hour = Math.floor(totalMinutes / 60)
    const minute = totalMinutes % 60
    const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`

    return {
      value,
      label: formatTimeTo12Hour(value),
    }
  },
)

// Parse various time input formats into { hour, minute, amPm, explicitPeriod }
// explicitPeriod indicates whether the user typed AM/PM explicitly
function parseTimeInput(
  input: string,
): {
  hour: number
  minute: number
  amPm: 'AM' | 'PM'
  explicitPeriod: boolean
} | null {
  if (!input || !input.trim()) return null

  const cleanInput = input.trim().toUpperCase()

  // Pattern 1: "2:30 PM", "2:30PM", "2:30 pm", "2:30p"
  const pattern12Hour = /^(\d{1,2}):(\d{2})\s*(A|P|AM|PM)$/i
  // Pattern 2: "14:30" (24-hour)
  const pattern24Hour = /^(\d{1,2}):(\d{2})$/
  // Pattern 3: "2PM", "2 PM", "2pm", "2p"
  const patternHourOnly = /^(\d{1,2})\s*(A|P|AM|PM)$/i
  // Pattern 4: "230pm" or "1430" (without colon)
  const patternNoColon = /^(\d{1,4})\s*(A|P|AM|PM)?$/i

  let hours: number
  let minutes: number
  let amPm: 'AM' | 'PM'

  const normalizePeriod = (period: string): 'AM' | 'PM' =>
    period.toUpperCase().startsWith('A') ? 'AM' : 'PM'

  let match = cleanInput.match(pattern12Hour)
  if (match) {
    hours = parseInt(match[1], 10)
    minutes = parseInt(match[2], 10)
    amPm = normalizePeriod(match[3])
    if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return null
    return { hour: hours, minute: minutes, amPm, explicitPeriod: true }
  }

  match = cleanInput.match(pattern24Hour)
  if (match) {
    hours = parseInt(match[1], 10)
    minutes = parseInt(match[2], 10)
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null
    amPm = hours >= 12 ? 'PM' : 'AM'
    if (hours === 0) hours = 12
    else if (hours > 12) hours -= 12
    return { hour: hours, minute: minutes, amPm, explicitPeriod: false }
  }

  match = cleanInput.match(patternHourOnly)
  if (match) {
    hours = parseInt(match[1], 10)
    minutes = 0
    amPm = normalizePeriod(match[2])
    if (hours < 1 || hours > 12) return null
    return { hour: hours, minute: minutes, amPm, explicitPeriod: true }
  }

  match = cleanInput.match(patternNoColon)
  if (match) {
    const numPart = match[1]
    const periodPart = match[2]
    const hasExplicitPeriod = !!periodPart

    if (numPart.length <= 2) {
      hours = parseInt(numPart, 10)
      minutes = 0
      if (periodPart) {
        amPm = normalizePeriod(periodPart)
        if (hours < 1 || hours > 12) return null
      } else {
        if (hours < 0 || hours > 23) return null
        amPm = hours >= 12 ? 'PM' : 'AM'
        if (hours === 0) hours = 12
        else if (hours > 12) hours -= 12
      }
    } else if (numPart.length === 3) {
      hours = parseInt(numPart[0], 10)
      minutes = parseInt(numPart.slice(1), 10)
      if (periodPart) {
        amPm = normalizePeriod(periodPart)
        if (hours < 1 || hours > 12) return null
      } else {
        if (hours < 0 || hours > 9) return null
        amPm = hours >= 12 ? 'PM' : 'AM'
      }
    } else if (numPart.length === 4) {
      hours = parseInt(numPart.slice(0, 2), 10)
      minutes = parseInt(numPart.slice(2), 10)
      if (periodPart) {
        amPm = normalizePeriod(periodPart)
        if (hours > 12) return null
      } else {
        if (hours < 0 || hours > 23) return null
        amPm = hours >= 12 ? 'PM' : 'AM'
        if (hours === 0) hours = 12
        else if (hours > 12) hours -= 12
      }
    } else {
      return null
    }

    if (minutes < 0 || minutes > 59) return null
    return {
      hour: hours,
      minute: minutes,
      amPm,
      explicitPeriod: hasExplicitPeriod,
    }
  }

  return null
}

// Convert parsed time to 24-hour format string (HH:mm)
function to24HourFormat(
  hour: number,
  minute: number,
  amPm: 'AM' | 'PM',
): string {
  let hour24 = hour
  if (amPm === 'PM' && hour < 12) hour24 += 12
  if (amPm === 'AM' && hour === 12) hour24 = 0
  return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

// Format 24-hour time string to display format (12-hour)
function formatTimeDisplay(time24: string): string {
  if (!time24) return ''
  return formatTimeTo12Hour(time24)
}

// When AM/PM was not explicitly typed, infer it from a reference time (e.g. start time).
// If the parsed time would be before or equal to the reference, flip AM/PM so end > start.
function inferAmPm(
  parsed: { hour: number; minute: number; amPm: 'AM' | 'PM' },
  referenceTime: string,
): 'AM' | 'PM' {
  const [refH, refM] = referenceTime.split(':').map(Number)
  const refMinutes = refH * 60 + refM

  const candidateMinutes =
    to24Hour(parsed.hour, parsed.amPm) * 60 + parsed.minute
  if (candidateMinutes > refMinutes) return parsed.amPm

  const flipped = parsed.amPm === 'AM' ? 'PM' : 'AM'
  const flippedMinutes = to24Hour(parsed.hour, flipped) * 60 + parsed.minute
  if (flippedMinutes > refMinutes) return flipped

  return parsed.amPm
}

function to24Hour(hour12: number, amPm: 'AM' | 'PM'): number {
  if (amPm === 'AM') return hour12 === 12 ? 0 : hour12
  return hour12 === 12 ? 12 : hour12 + 12
}

function floorDateToTimeOption(date: Date): string {
  const minutes =
    Math.floor(date.getMinutes() / TIME_OPTION_INCREMENT_MINUTES) *
    TIME_OPTION_INCREMENT_MINUTES

  return `${String(date.getHours()).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

function getSmartScrollTime(target: SmartTimeInputProps['scrollTarget']) {
  const date = new Date()
  if (target === 'past-hour') {
    date.setHours(date.getHours() - 1)
  }
  return floorDateToTimeOption(date)
}

interface SmartTimeInputProps {
  value?: string // 24-hour format (HH:mm)
  onChange: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  disabled?: boolean
  className?: string
  min?: string
  max?: string
  referenceTime?: string // 24-hour format start time, used to infer AM/PM when not explicitly typed
  scrollTarget?: 'current' | 'past-hour'
}

export function SmartTimeInput({
  value = '',
  onChange,
  onBlur,
  placeholder = '9:00 AM',
  disabled = false,
  className,
  referenceTime,
  scrollTarget = 'current',
}: SmartTimeInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  // Display value in 12-hour format for user-friendly editing
  const [textValue, setTextValue] = useState(() => formatTimeDisplay(value))
  const [isFocused, setIsFocused] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  // Track the original value for comparison
  const originalValueRef = useRef(value)
  const latestTextValueRef = useRef(textValue)
  const pendingValueRef = useRef<string | null>(null)
  const smartScrollTime = useMemo(
    () => getSmartScrollTime(scrollTarget),
    [scrollTarget],
  )

  const scrollValue = value || smartScrollTime

  // Update text value when value prop changes externally
  useEffect(() => {
    if (!isFocused) {
      if (pendingValueRef.current !== null) {
        if (value === pendingValueRef.current) {
          pendingValueRef.current = null
        } else {
          return
        }
      }

      const nextTextValue = formatTimeDisplay(value)
      setTextValue(nextTextValue)
      latestTextValueRef.current = nextTextValue
      originalValueRef.current = value
    }
  }, [value, isFocused])

  useEffect(() => {
    if (!isDropdownOpen) return

    const option = optionRefs.current[scrollValue]
    const list = listRef.current
    if (!option || !list) return

    const top =
      option.offsetTop - list.clientHeight / 2 + TIME_OPTION_HEIGHT / 2
    list.scrollTop = Math.max(0, top)
  }, [isDropdownOpen, scrollValue])

  // Handle blur - parse and submit only if changed
  const handleBlur = () => {
    setIsFocused(false)
    setIsDropdownOpen(false)

    const trimmedValue = latestTextValueRef.current.trim()
    const originalDisplay = formatTimeDisplay(originalValueRef.current)

    // If value hasn't changed, just format it nicely
    if (
      trimmedValue === originalDisplay ||
      trimmedValue === originalValueRef.current
    ) {
      setTextValue(formatTimeDisplay(originalValueRef.current))
      // Use setTimeout to ensure onBlur runs after React processes the state
      setTimeout(() => onBlur?.(), 0)
      return
    }

    // If empty and had a value, clear it
    if (!trimmedValue && originalValueRef.current) {
      pendingValueRef.current = ''
      onChange('')
      // Delay onBlur to run after onChange is processed by the form
      setTimeout(() => onBlur?.(), 0)
      return
    }

    // If empty and was empty, do nothing
    if (!trimmedValue) {
      setTimeout(() => onBlur?.(), 0)
      return
    }

    // Try to parse the input
    const parsed = parseTimeInput(trimmedValue)
    if (parsed) {
      // If AM/PM wasn't explicitly typed and we have a reference time, infer the period
      const finalAmPm =
        !parsed.explicitPeriod && referenceTime
          ? inferAmPm(parsed, referenceTime)
          : parsed.amPm
      const time24 = to24HourFormat(parsed.hour, parsed.minute, finalAmPm)
      const nextTextValue = formatTimeDisplay(time24)
      pendingValueRef.current = time24
      onChange(time24)
      setTextValue(nextTextValue)
      latestTextValueRef.current = nextTextValue
      // Delay onBlur to run after onChange is processed by the form
      setTimeout(() => onBlur?.(), 0)
    } else {
      // Invalid input - restore original
      setTextValue(formatTimeDisplay(originalValueRef.current))
      setTimeout(() => onBlur?.(), 0)
    }
  }

  // Handle key press
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      inputRef.current?.blur()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setTextValue(formatTimeDisplay(originalValueRef.current))
      inputRef.current?.blur()
    }
  }

  const handleOptionSelect = (nextValue: string) => {
    const nextTextValue = formatTimeDisplay(nextValue)
    pendingValueRef.current = nextValue
    onChange(nextValue)
    setTextValue(nextTextValue)
    latestTextValueRef.current = nextTextValue
    originalValueRef.current = nextValue
    setIsDropdownOpen(false)
    setTimeout(() => onBlur?.(), 0)
  }

  const handleFocus = () => {
    setIsFocused(true)
    if (!disabled) {
      setIsDropdownOpen(true)
    }
    // Select all text on focus for easy replacement
    setTimeout(() => {
      inputRef.current?.select()
    }, 0)
  }

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type="text"
        value={textValue}
        onChange={e => {
          setTextValue(e.target.value)
          latestTextValueRef.current = e.target.value
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onClick={() => {
          if (!disabled) setIsDropdownOpen(true)
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={cn('pr-8 tabular-nums', className)}
        autoComplete="off"
        aria-haspopup="listbox"
        aria-expanded={isDropdownOpen}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Show time options"
        className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        disabled={disabled}
        onMouseDown={event => {
          event.preventDefault()
          inputRef.current?.focus()
          setIsDropdownOpen(open => !open)
        }}
      >
        <CaretDownIcon className="h-3.5 w-3.5" />
      </button>
      {isDropdownOpen && !disabled && (
        <div
          ref={listRef}
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-[60] max-h-56 overflow-y-auto rounded-md border border-border/60 bg-background py-1 shadow-xl"
        >
          {TIME_OPTIONS.map(option => {
            const isSelected = option.value === value
            const isSmartTarget = !value && option.value === smartScrollTime

            return (
              <button
                key={option.value}
                ref={node => {
                  optionRefs.current[option.value] = node
                }}
                type="button"
                role="option"
                tabIndex={-1}
                aria-selected={isSelected}
                className={cn(
                  'flex h-9 w-full items-center justify-between px-3 text-left text-sm tabular-nums transition-colors hover:bg-muted',
                  isSelected && 'bg-muted font-medium text-foreground',
                  !isSelected &&
                    isSmartTarget &&
                    'bg-[hsl(var(--button-primary)/0.08)] text-foreground',
                )}
                onMouseDown={event => {
                  event.preventDefault()
                  handleOptionSelect(option.value)
                }}
              >
                <span>{option.label}</span>
                {isSmartTarget && (
                  <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {scrollTarget === 'past-hour' ? 'Past hour' : 'Current'}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
